import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { Observable } from 'rxjs';

// ─── Interfaces ──────────────────────────────────────────────────────────────

export interface Video {
  id: string;
  youtube_url: string;
  video_id: string;
  title: string;
  thumbnail_url: string;
  youtube_thumbnail_url?: string | null;
  thumbnail_source?: string | null;
  tags: string[];
  added_by: string;
  created_at: string;
  updated_at: string;
}

export interface VideoListResponse {
  items: Video[];
  total: number;
  skip: number;
  limit: number;
}

export interface VideoCreate {
  youtube_url: string;
  title?: string;
  tags?: string[];
}

export interface VideoUpdate {
  title?: string;
  tags?: string[];
  thumbnail_url?: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

@Injectable({
  providedIn: 'root',
})
export class VideoService {
  private http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/videos`;

  /** GET /api/v1/videos/ */
  list(skip = 0, limit = 50, search?: string): Observable<VideoListResponse> {
    const params: Record<string, string | number> = { skip, limit };
    if (search?.trim()) params['search'] = search.trim();
    return this.http.get<VideoListResponse>(`${this.apiUrl}/`, { params });
  }

  /** POST /api/v1/videos/ */
  add(payload: VideoCreate): Observable<Video> {
    return this.http.post<Video>(`${this.apiUrl}/`, payload);
  }

  /** PATCH /api/v1/videos/{id} */
  update(id: string, payload: VideoUpdate): Observable<Video> {
    return this.http.patch<Video>(`${this.apiUrl}/${id}`, payload);
  }

  /** PATCH /api/v1/videos/{id} — set a custom thumbnail URL */
  setThumbnailUrl(id: string, thumbnailUrl: string): Observable<Video> {
    return this.http.patch<Video>(`${this.apiUrl}/${id}`, { thumbnail_url: thumbnailUrl });
  }

  /** POST /api/v1/videos/{id}/thumbnail/generate */
  generateThumbnail(id: string): Observable<Video> {
    return this.http.post<Video>(`${this.apiUrl}/${id}/thumbnail/generate`, {});
  }

  /** POST /api/v1/videos/{id}/thumbnail/reset */
  resetThumbnail(id: string): Observable<Video> {
    return this.http.post<Video>(`${this.apiUrl}/${id}/thumbnail/reset`, {});
  }

  /** DELETE /api/v1/videos/{id} */
  delete(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  /** DELETE /api/v1/videos/ (batch) */
  batchDelete(ids: string[]): Observable<{ deleted: number }> {
    return this.http.delete<{ deleted: number }>(`${this.apiUrl}/`, { body: { ids } });
  }
}
